/**
 * `<WelcomeMoment>` — brief, tasteful, SKIPPABLE first-login welcome for a
 * freshly-provisioned paying tenant.
 *
 * Source-of-truth brief: ``backend/docs/cofounder_brief_paying_tenant_onboarding.md``.
 *
 * Gated on the same server-computed `first_run` flag as the getting-started
 * checklist (PAID + non-demo + not-dismissed + incomplete). One-time: once the
 * tenant skips/acknowledges it, a per-tenant localStorage marker keeps it from
 * re-appearing. Dismissing the whole checklist (server-side) also retires the
 * welcome via the `first_run` gate. Renders above the checklist on the
 * Dashboard.
 */
import { useState } from "react";
import { useGettingStarted } from "@/hooks/queries/auth/useGettingStarted";

const STORAGE_PREFIX = "certain.welcome.seen.";

function readSeen(tenantSlug: string | undefined): boolean {
  if (!tenantSlug) return true; // no tenant → never show
  try {
    return localStorage.getItem(STORAGE_PREFIX + tenantSlug) === "1";
  } catch {
    return false; // localStorage blocked → show once this session
  }
}

function markSeen(tenantSlug: string | undefined): void {
  if (!tenantSlug) return;
  try {
    localStorage.setItem(STORAGE_PREFIX + tenantSlug, "1");
  } catch {
    // ignore — best-effort one-time marker
  }
}

export function WelcomeMoment({
  tenantSlug,
  tenantName,
}: {
  tenantSlug: string | undefined;
  tenantName: string | null | undefined;
}): JSX.Element | null {
  const { data } = useGettingStarted();
  const [dismissed, setDismissed] = useState(() => readSeen(tenantSlug));

  // Only for a paid, non-demo, first-run tenant — and only once.
  if (!data || !data.first_run || dismissed) return null;

  const who = tenantName?.trim() || "there";

  const skip = (): void => {
    markSeen(tenantSlug);
    setDismissed(true);
  };

  return (
    <section
      data-testid="welcome-moment"
      aria-label="Welcome"
      className="overflow-hidden rounded-lg border border-border-default bg-surface-card p-4"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-text-emphasis">
            Welcome, {who}.
          </h2>
          <p className="mt-1 max-w-prose text-sm text-text-secondary">
            You&rsquo;re all set &mdash; here&rsquo;s how to get your first hire
            ranked.
          </p>
        </div>
        <button
          type="button"
          data-testid="welcome-skip"
          onClick={skip}
          className="shrink-0 rounded-md px-2.5 py-1 text-xs font-medium text-text-secondary hover:bg-surface-card-hover hover:text-text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
        >
          Skip
        </button>
      </div>
    </section>
  );
}
